/**
 * ISG Sales Intelligence — Frontend Entry Point
 *
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles (from @isg/ui-kit) ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';

// --- Services & Utils (from @isg/ui-kit barrel) ---
import { router, store, auth , checkEnvBanner } from '@isg/ui-kit';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';
import { mountCompanySwitcher, patchApiForViewingAs, getActingAs } from './company-switcher.js';
import { resolveSalesRole } from './services/sales-role.js';
import { accountUrl, partUrl } from './lib/links.js';
import { resolveEntity } from './lib/home-entity.js';

/* 🔴 The SPA must never run on the Outlook add-in's own pages (2026-09-25). If a service
   worker installed before /outlook/ was excluded answers one of them with this app, remove
   it and load the real page from the server — otherwise the whole sales app appears in
   Outlook's side panel instead of "Log as RFQ". */
// …and the sign-in window must come from the SERVER (its own security policy allows Office.js);
// if a service worker answered it from cache, remove the worker and ask the server.
// ⚠️ ONCE per window: a current worker still "controls" the page even while passing it to
// the server, so without the flag this would reload for ever.
const _swHealed = (() => { try { return sessionStorage.getItem('sales:outlook-sw-healed') === '1'; } catch { return true; } })();
if (!_swHealed && (window.location.pathname.startsWith('/outlook/')
    || (window.location.pathname === '/outlook-signin' && navigator.serviceWorker && navigator.serviceWorker.controller))) {
  try { sessionStorage.setItem('sales:outlook-sw-healed', '1'); } catch { /* private mode */ }
  const reload = () => window.location.replace(window.location.pathname + window.location.search);
  if (navigator.serviceWorker && navigator.serviceWorker.getRegistrations) {
    navigator.serviceWorker.getRegistrations()
      .then((regs) => Promise.all(regs.map((r) => r.unregister())))
      .finally(reload);
  } else {
    reload();
  }
  throw new Error('not the SPA: reloading the Outlook add-in page from the server');
}

/* Standalone "Lead Capture" PWA: on /capture, swap in its own manifest so the
   installed app is "Lead Capture" and opens straight to /capture (not the whole
   Sales CRM). Runs at module load — early enough for the install prompt. */
if (window.location.pathname === '/capture') {
  const m = document.querySelector('link[rel="manifest"]');
  if (m) m.setAttribute('href', '/lead-capture.webmanifest');
  document.title = 'ISG Lead Capture';
  let t = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (!t) { t = document.createElement('meta'); t.setAttribute('name', 'apple-mobile-web-app-title'); document.head.appendChild(t); }
  t.setAttribute('content', 'Lead Capture');
}

/* SSO: capture handoff from intranet broker */
auth.captureSSOHandoff();

// --- App-specific styles ---
import './styles/companion.css';
import { renderToolbar, renderBottomBar } from './components/shell.js';

// --- Lazy-loaded pages ---
const pages = {
  dashboard:      () => import('./pages/dashboard.js'),
  login:          () => import('./pages/login.js'),
  outlookSignin:  () => import('./pages/outlook-signin.js'),
  customers:      () => import('./pages/customers.js'),
  customerDetail: () => import('./pages/customer-detail.js'),
  prospects:      () => import('./pages/prospects.js'),
  prospect:       () => import('./pages/prospect.js'),
  meetingPlan: () => import('./pages/meeting-plan.js'),
  sourcing: () => import('./pages/sourcing.js'),
  pipeline:       () => import('./pages/pipeline.js'),
  revenue:        () => import('./pages/revenue.js'),
  opportunities:  () => import('./pages/opportunities.js'),
  marketInsights: () => import('./pages/market-insights.js'),
  showLeads:      () => import('./pages/show-leads.js'),
  newLead:        () => import('./pages/new-lead.js'),
  repCodes:       () => import('./pages/rep-codes.js'),
  part:           () => import('./pages/part.js'),
  parts:          () => import('./pages/parts.js'),
  projects:       () => import('./pages/projects.js'),
  team:           () => import('./pages/team.js'),
  inbox:          () => import('./pages/inbox.js'),
  today:          () => import('./pages/today.js'),
  approvals:      () => import('./pages/approvals.js'),
};

// ── Navigation config ────────────────────────────────────────────────
// ⚠️ Grouped the way a rep works, not the way the app grew (Callum, 2026-09-21).
// The rail was Workstream A's analytics nav with Companion features bolted on, and
// the result was that the part lookup, the cross-reference register and the whole
// quote studio had NO rail entry at all — reachable only through Ctrl+K or by
// clicking a part number on an existing line. Built is not the same as surfaced.
// The plan's shape is Today · Accounts · Quotes · Leads · Team; Today arrives with
// Phase 3, so its slot is simply absent rather than an empty page.
const NAV_CONFIG = [
  {
    section: null,
    items: [
      { path: '/', label: 'Dashboard', icon: 'layout-dashboard' },
      // The plan's rail is Today · Accounts · Quotes · Leads · Team. Today's slot was
      // deliberately ABSENT rather than an empty page until it existed (2026-09-23).
      { path: '/today', label: 'Today', icon: 'activity' },
    ],
  },
  {
    section: 'Accounts',
    items: [
      { path: '/customers', label: 'Customers', icon: 'users' },
      { path: '/prospects', label: 'Prospects', icon: 'user-plus' },
      { path: '/parts', label: 'Parts', icon: 'package' },
      { path: '/sourcing', label: 'Sourcing', icon: 'search' },
      { path: '/opportunities', label: 'Opportunities', icon: 'target' },
      { path: '/projects', label: 'Projects', icon: 'briefcase' },
    ],
  },
  {
    section: 'Quotes & orders',
    items: [
      { path: '/pipeline', label: 'Quotes & orders', icon: 'git-merge' },
      { path: '/approvals', label: 'Approvals', icon: 'check-square' },
      { path: '/revenue', label: 'Revenue', icon: 'currency-pound' },
    ],
  },
  {
    // The inbox is the front door: every source in one list, with who has each one
    // and how long it has waited. Show leads stays for its own detail (conversion
    // references, the CSV, match suggestions). The Web enquiries page was folded INTO the
    // inbox on 2026-09-28 (Callum) — /web-requests now redirects there.
    section: 'Leads',
    items: [
      { path: '/leads', label: 'Inbox', icon: 'inbox' },
      { path: '/show-leads', label: 'Show leads', icon: 'clipboard-list' },
      { path: '/new-lead', label: 'New lead', icon: 'user-plus' },
    ],
  },
  {
    // Market insights (Callum, 2026-10-26: "My patch needs to be changed to market insights and be
    // moved into its own section"). The rep's slice of Market Intelligence: what to do this week,
    // the markets their accounts are in, who stopped buying, what is happening at real companies,
    // and new business near them. The business-wide picture is BI › Markets.
    section: 'Market insights',
    items: [
      { path: '/market-insights', label: 'Market insights', icon: 'globe' },
    ],
  },
  {
    // Everyone with Sales access can see who holds which code; only sales
    // directors and admins get the buttons (the backend enforces it).
    section: 'Team',
    items: [
      { path: '/team/rep-codes', label: 'Rep codes', icon: 'user-check' },
    ],
  },
];

/* 🔴 "My team" is offered to whoever the ORG CHART says manages somebody — not to
   the `sales_director` grant, which exactly one of the eight sales managers holds
   (Callum, 2026-09-21). The server answers that question (`/team/available`,
   cached per person) and enforces it; a rail entry that refuses the person who
   clicks it is worse than no entry. Asked once per app load, and FAILS CLOSED:
   if the answer does not arrive, the entry is simply absent and /team still
   explains itself to anyone who types it. */
function navConfig({ team } = {}) {
  const config = NAV_CONFIG.map(section => ({ ...section, items: [...section.items] }));
  if (team) {
    config.find(s => s.section === 'Team').items.unshift(
      { path: '/team', label: 'My team', icon: 'network' });
  }
  return config;
}

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">Something went wrong</p>
          <p>${(err.message || 'Failed to load page.').replace(/[<>&"']/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;',"'":'&#39;'}[c]))}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">Reload</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell(shellOptions = {}) {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'Sales Intelligence' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: navConfig(shellOptions),
    appTitle: 'Sales Intelligence',
    fallbackInitial: 'S',
    collapsible: true,
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  // Companion shell (0.14): Find + as-of chip + New above every page; bottom bar on phones.
  const column = document.createElement('div');
  column.style.cssText = 'display:flex;flex-direction:column;flex:1;min-width:0;';
  column.appendChild(renderToolbar());
  column.appendChild(main);

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(column);
  app.appendChild(layout);
  app.appendChild(renderBottomBar());
  // "Viewing as" — platform admins pick a company; the server decides who may (company-switcher.js)
  mountCompanySwitcher();
  mountAssistant();

}

/** The sales assistant — how the app works and where its numbers come from
 *  (isg-figures Phase 2). ui-kit's chat window, backed by app/blueprints/assistant.py
 *  on the LOCAL model. Offered only when the AI capability check says it is on,
 *  so nobody is shown a window that would refuse them. Never throws. */
async function mountAssistant() {
  try {
    const { salesApi } = await import('./services/sales-api.js');
    const caps = await salesApi.getAiCapabilities();
    if (!((caps && (caps.data || caps)) || {}).ai_enabled) return;
    const { renderAgentWidget } = await import('@isg/ui-kit');
    const mount = document.createElement('div');
    mount.id = 'ai-assistant-mount';
    document.body.appendChild(mount);
    renderAgentWidget(mount, {
      appSlug: 'sales',
      title: 'Sales assistant',
      placeholder: 'Ask how a figure is worked out, or how to use the app…',
      attachments: false,
      suggestions: [
        'How is Revenue FYTD worked out?',
        'What does Live quotes include?',
        'Why might my revenue differ from finance?',
        'Who counts as the owner of an account?',
      ],
    });
  } catch (err) { /* no assistant beats a broken shell */ }
}

/** Does the org chart give this person a team? Never throws — no answer, no entry. */
async function teamAvailable() {
  try {
    const { salesApi } = await import('./services/sales-api.js');
    const res = await salesApi.getTeamAvailable();
    return !!((res || {}).data || {}).available;
  } catch (err) {
    return false;
  }
}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  // Before any page fetches: every request carries the admin's chosen company, if any.
  patchApiForViewingAs();
  // Pre-load branding so sidebar logo is ready
  await loadBranding().catch(() => {});
  // The Outlook add-in's sign-in window: a signed-out visitor goes through /login and is
  // sent back here afterwards (pages/login.js reads this key; pages/outlook-signin.js).
  if (window.location.pathname === '/outlook-signin') {
    try { sessionStorage.setItem('sales:after-login', '/outlook-signin'); } catch { /* private mode */ }
  }
  const hasSession = await auth.checkSession();

  // Store sales role from user claims for conditional UI.
  // Read app_access.sales.role — `user.role` is the PLATFORM role and shares
  // value names with ours ('viewer'), so falling back to it silently demoted
  // every sales_director to the scoped UI. See services/sales-role.js.
  const user = store.get('user') || {};
  store.set('salesRole', resolveSalesRole(user));
  // Acting as a person (platform admins, demos) is READ-ONLY — the UI hides writes too, so
  // a demo never shows a button that answers "you cannot". A stale choice on a non-admin
  // (a shared browser) is dropped rather than failing every call.
  // (who may act as whom is the SERVER's call — admins anyone, managers their own team; the
  // switcher drops a choice the server no longer offers)
  if (getActingAs()) store.set('salesRole', 'viewer');

  // Lead capture is open to every signed-in ISG user (app_registry is_public),
  // so anyone WITHOUT full Sales access is a capture-only user and should always
  // land on the standalone capture page — even if their installed PWA launches
  // at '/', or they type the bare domain. Without this they'd fall through to
  // the CRM shell and hit the sales guard's 403 access-denied screen.
  // No explicit lead-capture grant is required; the grant only still matters for
  // Sales itself.
  const _aa = user.app_access || {};
  const leadCaptureOnly = user.role !== 'admin'
    && !(_aa['sales'] && _aa['sales'].visible);

  // Define routes
  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/customers', (params) => renderPage(pages.customers, params))
    .on('/prospects', (params) => renderPage(pages.prospects, params))
    .on('/approvals', (params) => renderPage(pages.approvals, params))
    .on('/prospects/:id', (params) => renderPage(pages.prospect, params))
    // Accounts are addressed WITH their entity (plan: /a/:entity/:code). The old
    // /customers/:code links still work: they are rewritten to the entity the
    // reader's filter bar was on, which is what they always meant.
    .on('/a/:entity/:code', (params) => renderPage(pages.customerDetail, params))
    .on('/meeting-plans/:id', (params) => renderPage(pages.meetingPlan, params))
    .on('/sourcing', (params) => renderPage(pages.sourcing, params))
    .on('/sourcing/:entity/:part', (params) => renderPage(pages.sourcing, params))
    .on('/parts', (params) => renderPage(pages.parts, params))
    .on('/p/:entity/:part', (params) => renderPage(pages.part, params))
    // A link with no entity (a legacy /customers/<code>, or accountUrl in a fresh tab): ask the
    // server for the person's HOME entity, then go — never a guessed 'uk' (sweep R2 #48)
    .on('/customers/:code', async (params) => {
      const e = await resolveEntity();
      if (!e) return renderPage(pages.customers, {});
      history.replaceState(null, '', accountUrl(params.code, e) + window.location.search);
      return router.resolve();
    })
    .on('/part', async () => {
      const qs = new URLSearchParams(window.location.search);
      const part = qs.get('n');
      const e = await resolveEntity();
      if (!part || !e) return renderPage(pages.parts, {});
      qs.delete('n');
      history.replaceState(null, '', partUrl(part, e, {
        customer: qs.get('customer') || undefined, qty: qs.get('qty') || undefined, by: qs.get('by') || undefined }));
      return router.resolve();
    })
    .on('/projects', (params) => renderPage(pages.projects, params))
    .on('/pipeline', (params) => renderPage(pages.pipeline, params))
    .on('/revenue', (params) => renderPage(pages.revenue, params))
    .on('/opportunities', (params) => renderPage(pages.opportunities, params))
    .on('/market-insights', (params) => renderPage(pages.marketInsights, params))
    // My patch became Market insights on 2026-10-26: old links and bookmarks land on it
    .on('/my-patch', () => {
      history.replaceState(null, '', '/market-insights' + window.location.search);
      return router.resolve();
    })
    .on('/today', (params) => renderPage(pages.today, params))
    .on('/leads', (params) => renderPage(pages.inbox, params))
    // the Web enquiries page lives in the inbox now (2026-09-28): old links and bookmarks land
    // on the enquiry they named, or on the inbox showing web enquiries
    .on('/web-requests', () => {
      const ev = new URLSearchParams(window.location.search).get('event');
      history.replaceState(null, '', ev ? `/leads?open=${encodeURIComponent(`web:${ev}`)}` : '/leads?source=web');
      return router.resolve();
    })
    .on('/show-leads', (params) => renderPage(pages.showLeads, params))
    .on('/new-lead', (params) => renderPage(pages.newLead, params))
    .on('/team', (params) => renderPage(pages.team, params))
    .on('/team/rep-codes', (params) => renderPage(pages.repCodes, params))
    .on('/capture', (params) => renderPage(pages.newLead, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Auth guard — redirects unauthenticated users to login
  router.before((path) => {
    // An AUTHENTICATED user must never be left on the login page. Waving
    // /login through unconditionally renders the login FORM inside the app
    // shell — the app looks signed in and signed out at once, and only a
    // manual reload clears it. Not an edge case: api.js bounces here on any
    // 401 while the httpOnly cookie usually survives, so the reload lands
    // authenticated; the SSO broker's ?next and plain bookmarks reach it too.
    // Fixed in isg-app-template 2026-07-18; back-propagated 2026-07-31.
    if (path === '/login' && store.get('isAuthenticated')) {
      router.navigate('/');
      return false;
    }
    if (path === '/login') return true;
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    // No session — show login
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    /* AWAIT the render. `render()` is async — it fetches /auth/config before it
     * writes any markup — so without this, applyBranding() below runs against an
     * EMPTY container: `.login-hero-logo` doesn't exist yet, the selector misses,
     * and the page keeps the placeholder LETTER instead of the ISG logo. Silent:
     * nothing errors, the branding call just finds nothing to brand. */
    await module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else if (window.location.pathname === '/outlook-signin') {
    // Standalone: no shell — it is a small window inside Outlook's sign-in dialog.
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const mod = await pages.outlookSignin();
    await mod.render(document.getElementById('main-content'));
  } else if (window.location.pathname === '/capture' || leadCaptureOnly) {
    if (window.location.pathname !== '/capture') window.history.replaceState(null, '', '/capture');
    // Standalone "Lead Capture" launcher app — just the capture page, no CRM shell.
    // Lets people with lead_capture access capture leads without the full sales app.
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main" style="min-height:100vh"></main>';
    const mod = await pages.newLead();
    await mod.render(document.getElementById('main-content'));
    applyBranding();
  } else {
    // Authenticated — render app shell and resolve current route
    renderAppShell({ team: await teamAvailable() });
    checkEnvBanner(); // beta/test banner (no-op on live)
    await router.resolve();
    applyBranding();
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>Failed to load the application. Please refresh.</p>
      <button class="btn btn-primary" onclick="location.reload()">Reload</button>
    </div>
  `;
});
